import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';

interface ExcelViewerProps {
  file?: File;
  url?: string;
  fileName?: string;
}

interface SheetData {
  name: string;
  rows: string[][];
  colWidths: number[];
}

export const ExcelViewer: React.FC<ExcelViewerProps> = ({ file, url, fileName }) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSheets([]);
    setActiveSheet(0);
    setError(null);

    const parseBuffer = (data: ArrayBuffer) => {
      try {
        const workbook = XLSX.read(data, { type: 'array', cellDates: true, raw: false });
        const parsed: SheetData[] = workbook.SheetNames.map((name) => {
          const ws = workbook.Sheets[name];
          const rows: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];
          const colCount = Math.max(...rows.map(r => r.length), 0);
          const colWidths = Array.from({ length: colCount }, (_, c) =>
            Math.min(Math.max(...rows.map(r => String(r[c] ?? '').length), 4), 40)
          );
          return { name, rows, colWidths };
        });
        setSheets(parsed);
      } catch {
        setError('No se pudo leer el archivo');
      }
    };

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => { if (e.target?.result) parseBuffer(e.target.result as ArrayBuffer); };
      reader.onerror = () => setError('Error al leer el archivo');
      reader.readAsArrayBuffer(file);
    } else if (url) {
      fetch(url)
        .then(r => r.arrayBuffer())
        .then(parseBuffer)
        .catch(() => setError('No se pudo cargar el archivo'));
    }
  }, [file, url]);

  const sheet = sheets[activeSheet];

  return (
    <div className="flex flex-col h-full w-full">
      {/* Toolbar */}
      <div className="flex items-center px-4 py-2 border-b border-border bg-muted shrink-0 gap-2">
        <div className="flex-1 min-w-0">
          {(fileName ?? file?.name) && (
            <span className="text-xs text-text-subtle font-sans truncate block" title={fileName ?? file?.name}>
              {fileName ?? file?.name}
            </span>
          )}
        </div>
        {sheets.length > 1 && (
          <div className="flex gap-1 shrink-0">
            {sheets.map((s, i) => (
              <button
                key={s.name}
                onClick={() => setActiveSheet(i)}
                className={`text-xs px-2 py-0.5 rounded font-sans transition-colors ${
                  i === activeSheet
                    ? 'bg-primary text-white'
                    : 'bg-background border border-border text-text-subtle hover:bg-muted'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table area */}
      <div className="flex-1 overflow-auto bg-background min-h-0">
        {error ? (
          <div className="flex items-center justify-center h-full text-destructive font-sans text-sm">{error}</div>
        ) : !sheet ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="h-4 w-32 bg-muted rounded animate-pulse" />
              <div className="h-4 w-48 bg-muted rounded animate-pulse" />
              <div className="h-4 w-40 bg-muted rounded animate-pulse" />
            </div>
          </div>
        ) : sheet.rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-text-subtle font-sans text-sm">Hoja vacía</div>
        ) : (
          <table className="border-collapse text-xs font-sans w-max min-w-full">
            <tbody>
              {sheet.rows.map((row, ri) => (
                <tr key={ri} className={ri === 0 ? 'bg-muted sticky top-0 z-10' : 'hover:bg-muted/50'}>
                  {/* Row number gutter */}
                  <td className="border border-border px-1.5 py-1 text-text-subtle text-right select-none sticky left-0 bg-muted z-10 min-w-[2rem]">
                    {ri + 1}
                  </td>
                  {Array.from({ length: sheet.colWidths.length }, (_, ci) => {
                    const cell = String(row[ci] ?? '');
                    const isHeader = ri === 0 && cell.trim() !== '';
                    return (
                      <td
                        key={ci}
                        title={cell}
                        className={`border border-border px-2 py-1 truncate max-w-[240px] ${
                          isHeader ? 'font-semibold text-text' : 'text-text'
                        }`}
                        style={{ minWidth: `${Math.max(sheet.colWidths[ci] * 7, 40)}px` }}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ExcelViewer;
